import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MsalService } from '@azure/msal-angular';

export interface UserDetails {
  id?: string;
  username?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  claims?: any;
  tenantId?: string;
  isAdmin?: boolean;
  extension_CandidateId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  constructor(private http: HttpClient, private authService: MsalService) {}

  /**
   * Gets the active account from MSAL
   * @returns The active account or null if no account is active
   */
  getActiveAccount(): any {
    try {
      let account = this.authService.instance.getActiveAccount();
      if (!account) {
        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0) {
          account = accounts[0];
          this.authService.instance.setActiveAccount(account);
          return account;
        } else {
          return null;
        }
      }
      return account;
    } catch (error) {
      console.error('Error getting active account:', error);
      return null;
    }
  }

  /**
   * Gets user details from the active account
   * @returns UserDetails object with user information
   */
  getUserDetails(): UserDetails | null {
    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      const idTokenClaims = account.idTokenClaims;
      
      // Extract user details from token claims
      const userDetails: UserDetails = {
        id: idTokenClaims.oid || idTokenClaims.sub,
        username: account.username,
        email: account.username || idTokenClaims.emails?.[0],
        name: idTokenClaims.name,
        firstName: idTokenClaims.given_name,
        lastName: idTokenClaims.family_name,
        roles: idTokenClaims.roles || [],
        claims: idTokenClaims,
        tenantId: idTokenClaims.tid,
        extension_CandidateId: idTokenClaims.extension_CandidateId,
        isAdmin: (idTokenClaims.roles || []).includes('Admin')
      };

      return userDetails;
    } catch (error) {
      console.error('Error extracting user details:', error);
      return null;
    }
  }

  /**
   * Gets the raw ID token
   * @returns The ID token string or null if not available
   */
  getIdToken(): string | null {
    try {
      const account = this.getActiveAccount();
      if (!account) {
        return null;
      }
      
      return account.idToken || null;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  /**
   * Gets a specific claim from the token
   * @param claimName The name of the claim to retrieve
   * @returns The claim value or null if not found
   */
  getClaim(claimName: string): any {
    try {
      const account = this.getActiveAccount();
      if (!account || !account.idTokenClaims) {
        return null;
      }
      
      return account.idTokenClaims[claimName] || null;
    } catch (error) {
      console.error(`Error getting claim ${claimName}:`, error);
      return null;
    }
  }

  /**
   * Checks if the user has a specific role
   * @param role The role to check
   * @returns True if the user has the role, false otherwise
   */
  hasRole(role: string): boolean {
    const userDetails = this.getUserDetails();
    if (!userDetails || !userDetails.roles) {
      return false;
    }
    
    return userDetails.roles.includes(role);
  }

  /**
   * Gets the candidate ID from the token
   * @returns The candidate ID or null if not available
   */
  getCandidateId(): number | null {
    return this.getClaim('extension_CandidateId');
  }
}